import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, MessageSquare, Video } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Lovable AI integration for personalized interview coaching
export default function InterviewPrep() {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [interviewType, setInterviewType] = useState("behavioral");
  const [jobRole, setJobRole] = useState("");
  const [userBackground, setUserBackground] = useState("");
  const [coachingScript, setCoachingScript] = useState<string[]>([]);
  const [tips, setTips] = useState<string[]>([]);

  const handleGenerateCoaching = async () => {
    if (!jobRole || !userBackground) {
      toast({
        title: "Information Required",
        description: "Please provide the job role and your background.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      // GDPR Article 25: Privacy by design - AI processing without persistent storage
      const { data, error } = await supabase.functions.invoke("resume-assistant", {
        body: {
          stepType: "interview",
          userData: {
            interviewType,
            jobRole,
            userBackground,
          },
        },
      });

      if (error) throw error;

      // Parse AI response
      const result = data.result;
      setCoachingScript(result.questions || []);
      setTips(result.tips || []);

      toast({
        title: "Coaching Ready! ✨",
        description: "Your personalized interview prep has been generated.",
      });
    } catch (error: any) {
      console.error("Coaching error:", error);
      toast({
        title: "AI Processing Error",
        description: error.message || "Failed to generate coaching. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Interview Coach
          </CardTitle>
          <CardDescription>
            Get personalized coaching scripts to ace your next interview
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Interview Type</label>
            <Select value={interviewType} onValueChange={setInterviewType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="behavioral">Behavioral Interview</SelectItem>
                <SelectItem value="technical">Technical Interview</SelectItem>
                <SelectItem value="phone">Phone Screening</SelectItem>
                <SelectItem value="panel">Panel Interview</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Job Role You're Applying For</label>
            <Textarea
              placeholder="e.g., Warehouse Associate, Customer Service Representative..."
              value={jobRole}
              onChange={(e) => setJobRole(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Your Background & Concerns</label>
            <Textarea
              placeholder="Share your relevant experience and any concerns (e.g., employment gap, criminal record disclosure)..."
              value={userBackground}
              onChange={(e) => setUserBackground(e.target.value)}
              rows={4}
            />
          </div>

          <Button
            onClick={handleGenerateCoaching}
            disabled={isProcessing || !jobRole || !userBackground}
            className="w-full"
          >
            {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Sparkles className="h-4 w-4 mr-2" />
            Generate Coaching Script
          </Button>
        </CardContent>
      </Card>

      {/* Coaching Results */}
      {coachingScript.length > 0 && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Practice Questions
              </CardTitle>
              <CardDescription>
                Common questions for {interviewType.replace("-", " ")} interviews
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {coachingScript.map((question, idx) => (
                  <div key={idx} className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-2">Question {idx + 1}</p>
                    <p className="text-sm">{question}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {tips.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5 text-primary" />
                  Expert Tips
                </CardTitle>
                <CardDescription>
                  Personalized advice based on your background
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tips.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Badge variant="secondary" className="mt-0.5">
                        {idx + 1}
                      </Badge>
                      <p className="text-sm flex-1">{tip}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium">Pro Tip</p>
                  <p className="text-sm text-muted-foreground">
                    Practice your answers out loud. Record yourself to identify areas for improvement. 
                    Remember: honesty, confidence, and showing how you've grown are your greatest strengths.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
